import type NetworkOptimizationStore from '../../../lib/store';
import type mapboxgl from 'mapbox-gl';
import type {IAdequacyByCountyTableRow} from '../types';

/**
 * Strategy type for creating county fill layer paint configuration.
 * Each factory receives the network store and optional per-specialty adequacy data.
 */
export type CountyFillPaintFactory = (
    networkStore: NetworkOptimizationStore,
    countyData?: IAdequacyByCountyTableRow[]
) => mapboxgl.FillPaint;

const DEFAULT_FILL_PAINT: mapboxgl.FillPaint = {
    'fill-color': 'rgba(255, 255, 255, 0.05)',
    'fill-opacity': 0.4,
} as mapboxgl.FillPaint;

export const DESIGNATION_COLORS: Record<string, string> = {
    'Large Metro': '#0000FF',
    'Metro': '#00FF00',
    'Micro': '#FFFF00',
    'Rural': '#FFA500',
    'CEAC': '#FF0000',
};

/**
 * Builds a county_name → mapbox_id lookup from geography filters.
 * Used by adequacy-data-driven strategies to map API data to Mapbox vector tile features.
 */
function buildCountyNameToMapboxIdMap(networkStore: NetworkOptimizationStore): Map<string, string> {
    const entries = networkStore.getGeoFilterUniqueValuesByKey(
        'GEOGRAPHY',
        ['county_additional_info.county_name', 'county_additional_info.mapbox_id'],
        undefined,
        '|'
    );
    const map = new Map<string, string>();
    entries.forEach(entry => {
        const [countyName, mapboxId] = entry.split('|');
        if (countyName && mapboxId) {
            map.set(countyName, mapboxId);
        }
    });
    return map;
}

/**
 * Colors counties by their CMS designation (Large Metro, Metro, Micro, Rural, CEAC).
 * Used in the Introduction chapter's county designation map.
 */
export const createDesignationFillPaint: CountyFillPaintFactory = (networkStore) => {
    const uniqueCountyEntries = networkStore.getGeoFilterUniqueValuesByKey(
        'GEOGRAPHY',
        ['county_additional_info.mapbox_id', 'county_additional_info.county_designation'],
        undefined,
        '|'
    );

    const colorMap = new Map<string, string>();
    uniqueCountyEntries.forEach((entry) => {
        const [mapboxId, designation] = entry.split('|');
        if (mapboxId && designation && DESIGNATION_COLORS[designation]) {
            colorMap.set(mapboxId, DESIGNATION_COLORS[designation]);
        }
    });

    const designationColorPairs: string[] = [];
    colorMap.forEach((color, mapboxId) => {
        designationColorPairs.push(mapboxId, color);
    });

    return {
        'fill-color': designationColorPairs.length > 0
            ? ['match', ['get', 'mapbox_id'], ...designationColorPairs, 'rgba(255, 255, 255, 0.05)']
            : 'rgba(255, 255, 255, 0.05)',
        'fill-opacity': 0.4,
    } as mapboxgl.FillPaint;
};

export const POPULATION_GRADIENT = [
    'rgb(198, 219, 239)',
    'rgb(107, 174, 214)',
    'rgb(66, 146, 198)',
    'rgb(33, 113, 181)',
    'rgb(8, 69, 148)',
];

/**
 * Colors counties by member population (county_members) using a blue gradient (light → dark).
 * Requires per-specialty adequacy data from the /adequacy/specialty_code_by_county/batch/ API.
 */
export const createPopulationGradientFillPaint: CountyFillPaintFactory = (networkStore, countyData) => {
    if (!countyData || countyData.length === 0) return DEFAULT_FILL_PAINT;

    const nameToMapboxId = buildCountyNameToMapboxIdMap(networkStore);

    const populationMap: {mapboxId: string; population: number}[] = [];
    countyData.forEach((row) => {
        const mapboxId = nameToMapboxId.get(row.county_name);
        const population = parseInt(row.county_members, 10);
        if (mapboxId && !isNaN(population) && population > 0) {
            populationMap.push({mapboxId, population});
        }
    });

    if (populationMap.length === 0) return DEFAULT_FILL_PAINT;

    const populations = populationMap.map(p => p.population);
    const minPop = Math.min(...populations);
    const maxPop = Math.max(...populations);

    const colorMap = new Map<string, string>();
    populationMap.forEach(({mapboxId, population}) => {
        let index: number;
        if (maxPop === minPop) {
            index = Math.floor(POPULATION_GRADIENT.length / 2);
        } else {
            const ratio = (population - minPop) / (maxPop - minPop);
            index = Math.min(
                Math.floor(ratio * POPULATION_GRADIENT.length),
                POPULATION_GRADIENT.length - 1
            );
        }
        colorMap.set(mapboxId, POPULATION_GRADIENT[index]);
    });

    const colorPairs: string[] = [];
    colorMap.forEach((color, mapboxId) => {
        colorPairs.push(mapboxId, color);
    });

    return {
        'fill-color': colorPairs.length > 0
            ? ['match', ['get', 'mapbox_id'], ...colorPairs, 'rgba(255, 255, 255, 0.05)']
            : 'rgba(255, 255, 255, 0.05)',
        'fill-opacity': 0.5,
    } as mapboxgl.FillPaint;
};

export const PASS_COLOR = 'rgba(0, 100, 255, 0.6)';
export const FAIL_COLOR = 'rgba(220, 38, 38, 0.6)';

/**
 * Colors counties blue (pass) or red (fail) based on the adequacy status field.
 * status "1" = Pass (blue), status "0" = Fail (red).
 * Used in the specialty maps "NETWORK ADEQUACY - PASS / FAIL" map.
 */
export const createPassFailFillPaint: CountyFillPaintFactory = (networkStore, countyData) => {
    if (!countyData || countyData.length === 0) return DEFAULT_FILL_PAINT;

    const nameToMapboxId = buildCountyNameToMapboxIdMap(networkStore);

    const colorMap = new Map<string, string>();
    countyData.forEach((row) => {
        const mapboxId = nameToMapboxId.get(row.county_name);
        if (mapboxId) {
            colorMap.set(mapboxId, row.status === '1' ? PASS_COLOR : FAIL_COLOR);
        }
    });

    const colorPairs: string[] = [];
    colorMap.forEach((color, mapboxId) => {
        colorPairs.push(mapboxId, color);
    });

    return {
        'fill-color': colorPairs.length > 0
            ? ['match', ['get', 'mapbox_id'], ...colorPairs, 'rgba(255, 255, 255, 0.05)']
            : 'rgba(255, 255, 255, 0.05)',
        'fill-opacity': 0.5,
    } as mapboxgl.FillPaint;
};
